import { Modal, Text, Button, Group, Stack, Alert } from '@mantine/core';
import { PiWarning } from 'react-icons/pi';
import { useDeleteRole } from '@/models/adminUsers';
import type { Role } from '@/types/admin';

interface DeleteRoleConfirmationModalProps {
  opened: boolean;
  onClose: () => void;
  role: Role | null;
}

export function DeleteRoleConfirmationModal({
  opened,
  onClose,
  role,
}: DeleteRoleConfirmationModalProps) {
  const deleteMutation = useDeleteRole();

  const handleDelete = () => {
    if (role) {
      deleteMutation.mutate(role.id, {
        onSuccess: () => {
          onClose();
        },
      });
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Delete Role" size="md">
      <Stack gap="md">
        <Alert icon={<PiWarning size={20} />} color="red" variant="light">
          <Text size="sm" fw={500}>
            Warning: This action cannot be undone!
          </Text>
        </Alert>

        <Text size="sm">
          Are you sure you want to delete the role <strong>{role?.name}</strong>
          ?
        </Text>

        {role?.permissions && role.permissions.length > 0 && (
          <Text size="sm" c="dimmed">
            This role has {role.permissions.length} permission
            {role.permissions.length !== 1 ? 's' : ''} assigned to it.
          </Text>
        )}

        <Text size="sm" c="dimmed">
          Note: If any users are assigned to this role, the deletion will fail.
          Please remove all users from this role first.
        </Text>

        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            onClick={onClose}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleDelete}
            loading={deleteMutation.isPending}
          >
            Delete Role
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
